import { useEffect, useRef, useState } from "react";
import { Pin } from "lucide-react";
import api from "@/lib/api";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";

export const useSectionPins = (section, refreshKey) => {
  const [ids, setIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState(null);
  const trigger = useRef(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/pins/${section}`).then(({ data }) => { if (active) setIds(data.item_ids); })
      .catch(() => { if (active) setError({ title: "Could not load pins", message: "Please reopen this section to try again." }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [section, refreshKey]);
  const toggle = async (id) => {
    if (loading || pendingId) return;
    trigger.current = document.activeElement;
    setPendingId(id);
    try {
      const { data } = await api.put(`/pins/${section}/${id}`, { pinned: !ids.includes(id) });
      setIds(data.item_ids);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError({ title: requestError.response?.status === 409 ? "Pin limit reached" : "Could not update pins", message: typeof detail === "string" ? detail : detail?.message || "Please try again." });
    } finally { setPendingId(null); }
  };
  return { ids, loading, pendingId, toggle, error, dismiss: () => setError(null), trigger, removeDeleted: (id) => setIds((current) => current.filter((itemId) => itemId !== id)) };
};

export const PinButton = ({ section, item, pins }) => {
  const pinned = pins.ids.includes(item.id);
  return <button type="button" data-testid={`pin-${section}-${item.id}`} aria-label={`${pinned ? "Unpin" : "Pin"} ${item.title}`} aria-pressed={pinned} aria-busy={pins.pendingId === item.id} title={pinned ? "Unpin" : "Pin to top"} disabled={pins.loading || !!pins.pendingId} onClick={(event) => { event.stopPropagation(); pins.toggle(item.id); }} className={`relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors ${pinned ? "bg-ac-soft text-ac hover:bg-ac hover:text-white" : "text-stone-400 hover:bg-stone-100 hover:text-stone-700"}`}>
    <Pin aria-hidden="true" className="h-4 w-4" strokeWidth={2} fill={pinned ? "currentColor" : "none"} />
  </button>;
};

export const PinNotice = ({ section, pins }) => <AlertDialog open={!!pins.error} onOpenChange={(open) => { if (!open) pins.dismiss(); }}>
  <AlertDialogContent data-testid={`${section}-pin-notice`} className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onCloseAutoFocus={(event) => { if (pins.trigger.current?.isConnected) { event.preventDefault(); pins.trigger.current.focus(); } }}>
    <AlertDialogHeader className="text-left">
      <AlertDialogTitle data-testid={`${section}-pin-notice-title`} className="font-['Nunito']">{pins.error?.title}</AlertDialogTitle>
      <AlertDialogDescription data-testid={`${section}-pin-notice-message`}>{pins.error?.message}</AlertDialogDescription>
    </AlertDialogHeader>
    <AlertDialogFooter><AlertDialogAction data-testid={`${section}-pin-notice-close`} className="rounded-full bg-stone-900 hover:bg-stone-800 font-bold">Got it</AlertDialogAction></AlertDialogFooter>
  </AlertDialogContent>
</AlertDialog>;
